import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ContactTable from '../../components/ContactTable/ContactTable';
import MolstarViewer from '../../components/MolstarViewer/MolstarViewer';
import ContactMap from '../../components/ContactMap/ContactMap';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import GraphlqlQuery from '../../components/GraphlqlQuery/GraphlqlQuery';
import { ProgressSpinner } from 'primereact/progressspinner';
import { structureMap, getStructureContact, getInterContactsByStructure, getInterContactsByResidue, buildContactsCsvUrl, CONTACT_DISPLAY_LIMIT } from '../../ApiFunctions.js';
import BarChartContact from '../../components/BarChartContact/BarChartContact';
import AAHydrophobicityChart from '../../components/AAHydrophobicityChart/AAHydrophobicityChart';
import AAFrequencyChart from '../../components/AAFrequencyChart/AAFrequencyChart';
import AAPairHeatmap from '../../components/AAPairHeatmap/AAPairHeatmap';
import AADistanceBoxplot from '../../components/AADistanceBoxplot/AADistanceBoxplot';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import ContactNetwork from '../../components/ContactNetwork/ContactNetwork';
import multimerData from '../../mapa_alphafold.json';

const INTER_TAB_INDEX = 3;

const InterContactSection = ({ type, target, activeTabIndex, annotationMap, contactStructureLoading, contactStructureData }) => {

  const [visibleInterDialog, setVisibleInterDialog] = useState(false);

  // Dropdown (inter structures) + selected structure
  const [identifiers, setIdentifiers] = useState([]);
  const [loadedTab, setLoadedTab] = useState(false);
  const [structurePDBIdInter, setStructurePDBIdInter] = useState(undefined);

  // Data for the current view
  const [structureContacts, setStructureContacts] = useState([]); // loaded structure's inter contacts
  const [tableContacts, setTableContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  // "Too many" gates
  const [structureTooMany, setStructureTooMany] = useState(false);
  const [residueTooMany, setResidueTooMany] = useState(false);
  const [residueMode, setResidueMode] = useState(false);
  const [residueQuery, setResidueQuery] = useState('');
  const [residueInput, setResidueInput] = useState('');

  // Viewer / interaction
  const [positions, setPositions] = useState([]);
  const [chains, setChains] = useState([]);
  const [residues, setResidues] = useState([]);
  const [contactData, setContactData] = useState([]);
  const [interaction, setInteraction] = useState({ x: undefined, y: undefined, rowType: undefined, details: undefined });
  const [selectedUniProtAccession, setSelectedUniProtAccession] = useState(undefined);
  const [selectedRows, setSelectedRows] = useState(null);
  const [database, setDatabase] = useState("PDB");

  const multimerMap = useMemo(() => multimerData.default || multimerData, []);
  const uniac = useMemo(() => (target ? target.split('_')[0].toUpperCase() : ''), [target]);

  // Structure shown in Molstar: residue mode follows the selected row, else the dropdown.
  const viewerIdentifier = (residueMode && selectedRows) ? selectedRows.identifier : structurePDBIdInter;

  // Partner-protein options for the loaded structure
  const uniqueUniProtAccessions = useMemo(() => {
    if (!Array.isArray(structureContacts) || !structurePDBIdInter) return [];
    return [...new Set(structureContacts.map(c => c?.proteinUid?.toUpperCase()).filter(Boolean))];
  }, [structureContacts, structurePDBIdInter]);

  // ---- General-view (all contacts of the structure) for viewer + plots -------
  const fetchStructureData = useCallback(async (pdbId, selUniProt, options = { updateContactMapData: true }) => {
    const dataForPDB = structureContacts.filter(c =>
      (!selUniProt || c.proteinUid?.toUpperCase().trim() === selUniProt.toUpperCase().trim())
    );

    if (!dataForPDB.length) {
      if (options.updateContactMapData) setContactData([]);
      setPositions([]);
      setChains([]);
      return;
    }

    const contactPositions = [...new Set(dataForPDB.map(c => c.contactPosition))];
    const contactPositionsSource = [...new Set(dataForPDB.map(c => parseInt(c.anPosition?.split('_')[1])))];
    const proteinUid = dataForPDB[0].proteinUid;
    const uniProtSource = dataForPDB[0].anPosition?.split('_')[0];

    if (options.updateContactMapData) {
      setContactData(dataForPDB.map(c => ({
        contactPosition: c.contactPosition,
        anPosition: parseInt(c.anPosition?.split('_')[1]),
        details: c.details,
        distance: parseFloat(c.distance).toFixed(2)
      })));
    }

    try {
      if (pdbId.includes("AF-") && !selectedRows) {
        const isMultimer = /^AF-\d{16}$/.test(pdbId);
        let sourceChain = "A", targetChain = "B";
        if (isMultimer && uniProtSource && Array.isArray(multimerMap[uniProtSource])) {
          const stemOf = (f) => (f || '').replace(/-model_v\d+\.cif$/, '');
          const record = multimerMap[uniProtSource].find(r => stemOf(r.file) === pdbId);
          if (record?.chain) {
            sourceChain = record.chain;
            targetChain = record.chain === 'A' ? 'B' : 'A';
          }
        }
        const chainsSource = new Array(contactPositionsSource.length).fill(sourceChain);
        const chainsTarget = new Array(contactPositions.length).fill(targetChain);
        setPositions([...contactPositionsSource, ...contactPositions]);
        setChains([...chainsSource, ...chainsTarget]);
        setDatabase(isMultimer ? "AF" : "CO");
      } else if (!pdbId.includes("AF-") && !selectedRows) {
        const { mappedPositions: mappedSource, chains: chainsSource } = await structureMap(contactPositionsSource, pdbId, uniProtSource);
        const { mappedPositions: mappedTarget, chains: chainsTarget } = await structureMap(contactPositions, pdbId, proteinUid);
        setPositions([...mappedSource, ...mappedTarget]);
        setChains([...chainsSource, ...chainsTarget]);
        setDatabase("PDB");
      }
    } catch (error) {
      console.error('Error fetching structure data:', error);
      setChains([]);
      setPositions([]);
      setDatabase("PDB");
      setResidues([]);
    }
  }, [structureContacts, selectedRows, multimerMap]);

  // ---- Load the inter structure list when the tab opens ----------------------
    useEffect(() => {
    if (activeTabIndex !== INTER_TAB_INDEX || contactStructureLoading || !target) return;
    const identifiers = contactStructureData || [];
    setIdentifiers(identifiers);
    setStructurePDBIdInter(identifiers?.length > 0 ? identifiers[0] : undefined);
  }, [activeTabIndex, contactStructureLoading, contactStructureData, target]);


  // ---- Load the selected structure's inter contacts --------------------------
  useEffect(() => {
    if (!structurePDBIdInter || !target) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setResidueMode(false);
      setResidueTooMany(false);
      setResidueQuery('');
      setResidueInput('');
      setSelectedRows(null);
      setInteraction({ x: undefined, y: undefined, rowType: undefined, details: undefined });

      const { contacts, tooMany } = await getInterContactsByStructure(target, structurePDBIdInter);
      if (cancelled) return;

      setStructureTooMany(tooMany);
      setStructureContacts(tooMany ? [] : contacts);
      setTableContacts(tooMany ? [] : contacts);

      if (structurePDBIdInter.includes("AF-")) {
        setDatabase(/^AF-\d{16}$/.test(structurePDBIdInter) ? "AF" : "CO");
      } else {
        setDatabase("PDB");
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [structurePDBIdInter, target]);

  // ---- Sync the partner (UniProt Accession) with the loaded structure --------
  useEffect(() => {
    if (structurePDBIdInter && structureContacts.length > 0) {
      const accs = [...new Set(structureContacts.map(c => c?.proteinUid).filter(Boolean))];
      if (accs.length > 0) {
        setSelectedUniProtAccession(prev => (accs.includes(prev) ? prev : accs[0]));
      }
    }
  }, [structurePDBIdInter, structureContacts]);

  // ---- General view whenever structure / partner changes (structure mode) ----
  useEffect(() => {
    if (!residueMode && structurePDBIdInter && selectedUniProtAccession) {
      fetchStructureData(structurePDBIdInter, selectedUniProtAccession, { updateContactMapData: true });
    }
  }, [structurePDBIdInter, selectedUniProtAccession, residueMode, fetchStructureData]);

  // ---- Row selection highlight (derives chains/positions from the details) ---
  useEffect(() => {
    const { x, y, details } = interaction;
    if (x !== undefined && y !== undefined && details && viewerIdentifier) {
      try {
        const residue1 = details.split("|")[0].trim();
        const residue2 = details.split("|")[1].trim();
        const residueDetails1 = residue1.split("/");
        const residueDetails2 = residue2.split("/");

        setChains([residueDetails1[0], residueDetails2[0]]);
        setPositions([parseInt(residueDetails1[1]), parseInt(residueDetails2[1])]);
        setResidues([residueDetails1[2], residueDetails2[2]]);

        if (viewerIdentifier.includes("AF-")) {
          setDatabase(/^AF-\d{16}$/.test(viewerIdentifier) ? "AF" : "CO");
        } else {
          setDatabase("PDB");
        }
      } catch (error) {
        console.error('Error fetching interaction highlight:', error);
      }
    }
  }, [interaction, viewerIdentifier]);

  const handlePDBChange = useCallback((e) => {
    const selected = e.target.value;
    if (selected !== structurePDBIdInter) {
      setResidueMode(false);
      setResidueTooMany(false);
      setResidueQuery('');
      setResidueInput('');
      setInteraction({ x: undefined, y: undefined, distance: undefined, details: undefined });
      setSelectedRows(null);
      setStructurePDBIdInter(selected);
    }
  }, [structurePDBIdInter]);

  // Row click: only switch structure when NOT in residue mode; in residue mode
  // the table stays and the viewer follows the row via viewerIdentifier.
  const handleTableStructureChange = useCallback((id) => {
    if (!residueMode) setStructurePDBIdInter(id);
  }, [residueMode]);

  const interactionReset = useCallback(() => {
    setInteraction({ x: undefined, y: undefined, distance: undefined, details: undefined });
    setSelectedRows(null);
    if (!residueMode && structurePDBIdInter) {
      fetchStructureData(structurePDBIdInter, selectedUniProtAccession, { updateContactMapData: false });
    }
  }, [residueMode, structurePDBIdInter, selectedUniProtAccession, fetchStructureData]);

  const handleUniProtChange = useCallback((e) => {
    setSelectedUniProtAccession(e.target.value);
    setInteraction({ x: undefined, y: undefined, distance: undefined, details: undefined });
    setSelectedRows(null);
  }, []);

  // ---- Search by residue -----------------------------------------------------
  const handleResidueSearch = useCallback(async (anPosition) => {
    const value = (anPosition || '').trim();
    if (!value) return;
    setLoading(true);
    setResidueQuery(value);
    const { contacts, tooMany } = await getInterContactsByResidue(value);
    setResidueTooMany(tooMany);
    setResidueMode(true);
    setTableContacts(tooMany ? [] : contacts);
    if (!tooMany && contacts.length > 0) {
      const first = contacts[0];
      const firstAnPos = (typeof first.anPosition === 'string' && first.anPosition.includes('_'))
        ? parseInt(first.anPosition.split('_')[1], 10) : first.anPosition;
      setSelectedRows({ ...first, rowKey: `${first.anPosition}|${first.identifier}|${first.contactPosition}|${first.details}` });
      setInteraction({ x: firstAnPos, y: first.contactPosition, distance: first.distance !== undefined ? parseFloat(first.distance) : 0, rowType: 'main', details: first.details });
    } else {
      setSelectedRows(null);
      setInteraction({ x: undefined, y: undefined, rowType: undefined, details: undefined });
    }
    setLoading(false);
  }, []);

  const handleResidueClear = useCallback(() => {
    setResidueMode(false);
    setResidueTooMany(false);
    setResidueQuery('');
    setResidueInput('');
    setSelectedRows(null);
    setInteraction({ x: undefined, y: undefined, rowType: undefined, details: undefined });
    setTableContacts(structureContacts);
  }, [structureContacts]);

  const structureCsvUrl = buildContactsCsvUrl({ target, identifier: structurePDBIdInter, inter: true });
  const residueCsvUrl = buildContactsCsvUrl({ target, anPosition: residueQuery, inter: true });
  const activeCsvUrl = residueTooMany ? residueCsvUrl : structureCsvUrl;
  const isTooMany = residueTooMany || (structureTooMany && !residueMode);
  const showViewer = !loading && !isTooMany && !!viewerIdentifier &&
    ((residueMode && !!selectedRows) || (!residueMode && structureContacts.length > 0));
  const showPlots = !loading && !isTooMany && !residueMode && structureContacts.length > 0 && !!structurePDBIdInter;

  return (
    <>
      <h2 className="display-6 fw-bold mb-5">
        Inter-protein contacts
        <sup>
          <Button icon="pi pi-info" onClick={() => setVisibleInterDialog(true)} text size="small" className="btn-dialog" />
        </sup>
      </h2>

      <Dialog
        header="Inter-protein contacts"
        visible={visibleInterDialog}
        style={{ width: '50vw' }}
        onHide={() => setVisibleInterDialog(false)}
        position="top-right"
        draggable={false}
        resizable={false}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <p className="m-0 mb-2 text-justify">
          This section presents a list of inter-protein contacts derived from experimental structures calculated in-house. The table includes the following information: the residue position in the protein sequence, the UniProt accession number, the gene symbol, the Identifier of the 3D structure, the contact position, the distance, and the atoms involved in the contact. We define residue-residue contacts as those in which the Cβ atoms are spatially closer than 8 Å (Cα for glycine), which is a commonly used distance cutoff.
        </p>
        <GraphlqlQuery type={type} target={target} tab={activeTabIndex} />
        <p className="m-0 mb-1 text-justify">For more information, visit <Link to="/about/" target="_blank"><b> about </b></Link> or <Link to="/api/" target="_blank"><b> API </b></Link> page.</p>
      </Dialog>

      {/* Selectors: structure (+ partner) and residue search, side by side */}
      <div className="row mb-3">
        <div className="col-md-5 mb-2">
          <div className="bg-light rounded-3 p-3 border-start border-3 ">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label htmlFor="structureInter" className="form-label fw-bold mb-0 d-flex align-items-center gap-2 text-dark">
                <i className="pi pi-box text-primary"></i>
                <span>3D Structure</span>
              </label>
              {identifiers.length > 0 && (
                <span className="badge bg-white text-dark border fw-normal px-2 py-1 rounded-pill">
                  {identifiers.length} available
                </span>
              )}
            </div>

            <p className="fs--2 text-dark mb-3">
              Select a structure to view its inter-protein contacts, contact map and 3D viewer below.
            </p>

            <Dropdown
              inputId="structureInter"
              value={structurePDBIdInter}
              options={identifiers}
              onChange={handlePDBChange}
              filter
              placeholder="Select a structure"
              style={{ width: '100%' }}
              className="structure-dropdown"
              loading={contactStructureLoading}
              disabled={loading || identifiers.length === 0}

            />

            {uniqueUniProtAccessions.length > 1 && (
              <div className="mt-3">
                <label htmlFor="uniProtAccession" className="form-label fw-semibold text-dark">Partner (UniProt Accession)</label>
                <Dropdown
                  inputId="uniProtAccession"
                  value={selectedUniProtAccession}
                  options={uniqueUniProtAccessions}
                  onChange={handleUniProtChange}
                  filter
                  placeholder="Select"
                  style={{ width: '100%' }}
                  className="structure-dropdown"
                  disabled={loading}
                />
              </div>
            )}

            {structurePDBIdInter && !residueMode && (
              <div className="d-flex align-items-center justify-content-between mt-3 pt-2 border-top border-200 fs--2">
                <span className="fw-semibold text-dark">Showing structure:</span>
                <span className="fw-bold font-monospace bg-white px-2 py-1 rounded border text-primary">
                  {structurePDBIdInter.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Search by residue */}
        <div className="col-md-5 mb-2">
          <div className="bg-light rounded-3 p-3 border-start border-3 ">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label htmlFor="residueSearchInter" className="form-label fw-bold mb-0 d-flex align-items-center gap-2 text-dark">
                <i className="pi pi-search text-primary"></i>
                <span>Search by residue</span>
              </label>
            </div>

            <p className="fs--2 text-dark mb-3">
              Enter a residue (e.g. {uniac ? `${uniac}_290` : 'P17612_290'}) to see its inter-protein contacts across all structures.
            </p>

            <div className="d-flex gap-2">
              <InputText
                id="residueSearchInter"
                value={residueInput}
                onChange={(e) => setResidueInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResidueSearch(residueInput); }}
                placeholder="e.g. P17612_290"
                style={{ width: '100%' }}
                disabled={loading}
              />
              <Button
                icon="pi pi-search"
                onClick={() => handleResidueSearch(residueInput)}
                disabled={loading || !residueInput}
                tooltip="Search this residue across all structures"
              />
            </div>

            {residueMode && (
              <div className="d-flex align-items-center justify-content-between mt-3 pt-2 border-top border-200 fs--2">
                <span className="fw-semibold text-dark">Showing residue:</span>
                <span className="fw-bold font-monospace bg-white px-2 py-1 rounded border text-primary">
                  {residueQuery.toUpperCase()}
                </span>
                <Button label="Clear" icon="pi pi-times" text size="small" onClick={handleResidueClear} />
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="row mb-4 d-flex align-items-center justify-content-center py-4">
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="8" animationDuration=".5s" />
        </div>
      ) : isTooMany ? (
        <div className="row mb-4">
          <div className="col-12">
            <div className="bg-light rounded-3 p-4 border-start border-4 border-warning">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="rounded-circle bg-white border p-3 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                    <i className="pi pi-exclamation-triangle text-warning fs-4"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1 text-dark">High Density Contact Dataset</h5>
                    <p className="mb-0 fs--2 text-dark">
                      This selection has more than <strong>{CONTACT_DISPLAY_LIMIT.toLocaleString()}</strong> contacts — too many to render in table view. Download the full set as CSV instead.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href={activeCsvUrl}
                    className="p-button p-button-warning p-button-outlined d-inline-flex align-items-center gap-2 fw-bold text-decoration-none px-3 py-2"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="pi pi-download"></i>
                    <span>Download CSV</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row mb-4">
          <ContactTable
            contactData={tableContacts}
            setInteraction={setInteraction}
            setStructurePDBIdInter={handleTableStructureChange}
            interactionReset={interactionReset}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            annotationMap={annotationMap}
            setResidues={setResidues}
            setSelectedUniProtAccession={setSelectedUniProtAccession}
            type="inter"
          />
        </div>
      )}

      {/* Structure viewer — also shows in residue mode, following the clicked row */}
      {showViewer && (
        <>
          <h2 className="display-6 fw-bold mb-4">Structure</h2>
          <div className="row mb-4">
            <div className="col-md-2 mb-2">
              <div className="bg-light rounded-3 p-3">
                <div className="d-flex align-items-center mb-2">
                  <div className="burble-contact me-2"></div>
                  <p className="fs--2 text-justify m-0 text-dark">Contact position</p>
                </div>
                <p className="fs--2 text-justify mb-0 text-dark">
                  Click on the viewer plugin icons to access more advanced features
                  from <Link to="https://molstar.org/viewer-docs/" target="_blank">Mol*</Link>.
                </p>
              </div>
            </div>
            <div className="col-md-10">
              <div className="viewer-box">
                <MolstarViewer
                  pdbId={viewerIdentifier}
                  tab="inter-contacts"
                  contactPairs={tableContacts}
                  chain={chains}
                  positions={positions}
                  database={database}
                  distance={interaction.distance}
                  rowType={interaction.rowType}
                  residues={residues}
                  uniac={uniac}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Plots — structure mode only */}
      {showPlots && (
        <>
          <h2 className="display-6 fw-bold mb-4">Summary  |  {structurePDBIdInter.toUpperCase()} </h2>
          <div className="row mb-3">
            <div className="col-md-4"><ContactMap contactData={contactData} /></div>
            <div className="col-md-4"><BarChartContact contactData={contactData} /></div>
            <div className="col-md-4"><AAHydrophobicityChart contactData={contactData} /></div>
          </div>
          <div className="row mb-4">
            <div className="col-md-4"><AAFrequencyChart contactData={contactData} /></div>
            <div className="col-md-4"><AAPairHeatmap contactData={contactData} /></div>
            <div className="col-md-4"><AADistanceBoxplot contactData={contactData} /></div>
          </div>
          <div className="row">
            <ContactNetwork key="kd-bar-inter" contactData={contactData} structureId={structurePDBIdInter} id="kd-bar-inter" />
          </div>
        </>
      )}
    </>
  );
};

export default InterContactSection;
